/**
 * city_lights_material — the light carpet's two node materials (the night-sky realism wave, 2026-10-06, agent L):
 *
 *   createCityLightsMaterial — ONE instanced additive batch of constant-screen-size points (a Gaussian dot σ px), each
 *     a street head, a lit-window cluster, a floodlit face or a bio-glass fixture (lib/city_night_lights.ts encodes
 *     them). The VERTEX stage does the photometry once per point: E = I·T(d)/d² through the scene fog's own
 *     transmittance, the window's cosine lobe, the lit threshold against its class's fraction of the night, the photocell
 *     on/off, the near fade where a built town's real lamps take over, scintillation with the air path; the dot's peak
 *     luminance E / (Ω_px · 2πσ²) (flux conserved at any distance and resolution) → scene units through the per-frame
 *     emissiveNits scale (THE PHYSICAL-EMISSIVE CONTRACT), capped. A dead or dark point collapses outside the clip
 *     volume. THE DEPTH PULL: drawn depthPull(d) nearer the eye than it stands, so it wins against the ground and
 *     facade it sits on (standard float depth resolves ~120 m at 10 km on a 0.05 m near plane).
 *   createCityHazeMaterial — the town's own near-ground glow: a full-screen additive pass that extends the K light
 *     domes (the same skyglowLive() numbers the sky draws) BELOW the horizon onto the geometry behind and around a lit
 *     town, weighted by the share of the town's glowing air in front of the hit (the scene depth). Zero where the sky
 *     shows — the sky shader's dome is the sky's own (no double count).
 *
 * Both are markMrtOverlay'd (THE MRT OVERLAY LAW), fog-free (they apply the fog's transmittance themselves — three's fog
 * on an additive material would ADD the fog colour) and depthWrite-free. Mirrors: lib/city_night_lights.ts.
 */
import * as THREE from 'three/webgpu';
import {
  Fn, attribute, uniform, vec3, vec4, float, length, exp, max, min, dot, cos, sin, select, step, smoothstep, floor,
  cameraProjectionMatrix, cameraProjectionMatrixInverse, cameraWorldMatrix, cameraNear, cameraFar, modelViewMatrix,
  positionGeometry, screenSize, uv, varyingProperty, normalize, acos, atan, clamp, viewportDepthTexture, linearDepth, log,
} from 'three/tsl';
import { markMrtOverlay } from 'enginex/render/post/mrtOverlay.js';
import { geometryMask } from 'enginex/render/depth/skyMask.js';
import { SPRITE_SIGMA_PX, SPRITE_HALF_PX, POOL_CD, LOG_I_DECADES, HAZE_LAYER_M, HAZE_ART_K, KNEE_SLOPE, PULL_ULPS } from './city_night_lights.js';

type N = ReturnType<typeof float>;
type V = { value: number };

export interface CityLightUniforms {
  /** The camera's position in the batch's frame (metres). */
  cam: { value: THREE.Vector3 };
  /** tan(vertical fov / 2) of the drawing camera. */
  tanHalfFov: V;
  /** The scene fog's FogExp2 density (0 = clear). */
  fogDensity: V;
  /** Scene units per cd/m² (photometry.emissiveNits(1)), the knee and the cap (emissiveNits(∞) at KNEE_EV / the cap EV). */
  nitsScale: V;
  kneeScene: V;
  capScene: V;
  /** Lit fractions of the night by window class: home, trade, work, civic. */
  lit: { value: THREE.Vector4 };
  /** Street heads (photocells) and windows on, 0..1. */
  lampsOn: V;
  winOn: V;
  /** Seconds (the shimmer). */
  clock: V;
  /** An overall gain (1 = physical). */
  gain: V;
}

/** The carpet's material (an InstancedBufferGeometry with aPos vec3, aCol unorm8x4, aInfo unorm8x4 — 4 vertex buffers). */
export function createCityLightsMaterial(): { material: THREE.MeshBasicNodeMaterial; uniforms: CityLightUniforms } {
  const u: CityLightUniforms = {
    cam: uniform(new THREE.Vector3()) as unknown as { value: THREE.Vector3 },
    tanHalfFov: uniform(0.7) as unknown as V,
    fogDensity: uniform(0) as unknown as V,
    nitsScale: uniform(1) as unknown as V,
    kneeScene: uniform(1) as unknown as V,
    capScene: uniform(8) as unknown as V,
    lit: uniform(new THREE.Vector4(0.3, 0.3, 0.3, 0.3)) as unknown as { value: THREE.Vector4 },
    lampsOn: uniform(0) as unknown as V,
    winOn: uniform(0) as unknown as V,
    clock: uniform(0) as unknown as V,
    gain: uniform(1) as unknown as V,
  };
  const un = u as unknown as Record<keyof CityLightUniforms, N>;
  const mat = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, depthTest: true, blending: THREE.AdditiveBlending });
  mat.name = 'city-night-lights';
  mat.fog = false;
  markMrtOverlay(mat);
  const vLight = varyingProperty('vec3', 'vCityLight');
  const TAU = Math.PI * 2;
  const sig2 = SPRITE_SIGMA_PX * SPRITE_SIGMA_PX;
  mat.vertexNode = Fn(() => {
    const P = attribute('aPos', 'vec3');
    const C = attribute('aCol', 'vec4');
    const F = attribute('aInfo', 'vec4');
    const viewPos = modelViewMatrix.mul(vec4(P, 1));
    const d = length(viewPos.xyz).max(1).toVar('clD');
    const cls = floor(F.x.mul(255).add(0.5)).toVar('clCls');
    const faceB = floor(F.y.mul(255).add(0.5));
    const logB = floor(F.z.mul(255).add(0.5));
    const nearM = F.w.mul(255 * 16);
    const thrB = floor(C.w.mul(255).add(0.5)).toVar('clThr');
    const I = select(logB.greaterThan(0.5), exp(logB.sub(1).div(254).mul(LOG_I_DECADES * Math.LN10)), float(0));
    const toCam = un.cam.sub(P);
    const dc = length(toCam).max(1);
    // the lit rule: a window cluster by its threshold against its class's fraction (an aggregate SCALES by it)
    const frac = select(cls.lessThan(3.5), un.lit.x, select(cls.lessThan(4.5), un.lit.y, select(cls.lessThan(5.5), un.lit.z, un.lit.w)));
    const isWin = cls.greaterThan(2.5).and(cls.lessThan(6.5));
    const winLit = select(thrB.greaterThan(254.5), frac, step(thrB.div(254), frac));
    const lit = select(isWin, winLit.mul(un.winOn), un.lampsOn);
    // a window's cosine lobe about its face (0 behind it); omni otherwise
    const ang = faceB.sub(1).div(254).mul(TAU).sub(Math.PI);
    const lobe = select(faceB.greaterThan(0.5), max(float(0), cos(ang).mul(toCam.x).add(sin(ang).mul(toCam.z)).div(dc)), float(1));
    // a street head seen from above shows its lit pool
    const pool = select(cls.lessThan(1.5), max(float(0), toCam.y.div(dc)).mul(POOL_CD), float(0));
    const fade = smoothstep(nearM, nearM.mul(1.6).add(1), dc);
    const Ieff = I.add(pool).mul(lobe).mul(lit).mul(fade).mul(un.gain);
    const rho = un.fogDensity.mul(d);
    const E = Ieff.mul(exp(rho.mul(rho).negate())).div(d.mul(d));
    const pxAng = un.tanHalfFov.mul(2).div(screenSize.y);
    const L = E.div(pxAng.mul(pxAng).mul(TAU * sig2));
    const shDepth = smoothstep(float(1500), float(15000), d).mul(0.35);
    const ph = thrB.mul(0.37);
    const sh = float(1).add(shDepth.mul(sin(un.clock.mul(thrB.mul(0.013).add(5.3)).add(ph)).mul(sin(un.clock.mul(3.1).add(ph.mul(1.7))))));
    // THE KNEE (lib kneeCompress): linear to KNEE_EV over the key, logarithmic above, capped
    const x = L.mul(sh).mul(un.nitsScale).div(un.kneeScene.max(1e-9));
    const sceneL = min(select(x.lessThan(1), x, log(x.max(1)).mul(KNEE_SLOPE).add(1)).mul(un.kneeScene), un.capScene);
    const rgb = C.xyz;
    const lum = dot(rgb, vec3(0.2126, 0.7152, 0.0722)).max(1e-3);
    vLight.assign(rgb.div(lum).mul(sceneL));
    const clip = cameraProjectionMatrix.mul(viewPos);
    // THE DEPTH PULL (lib depthPull): PULL_ULPS steps of the depth precision at d (∝ d² / near), ≤ a quarter of d
    const pull = min(d.mul(0.25), d.mul(0.003).add(2).add(d.mul(d).mul(PULL_ULPS * 6e-8).div(cameraNear.max(1e-3))));
    const cp = cameraProjectionMatrix.mul(vec4(viewPos.xyz.mul(float(1).sub(pull.div(d))), 1));
    const zN = cp.z.div(cp.w);
    const off = positionGeometry.xy.mul(4 * SPRITE_HALF_PX).div(screenSize).mul(clip.w);
    return select(sceneL.greaterThan(1e-7), vec4(clip.xy.add(off), zN.mul(clip.w), clip.w), vec4(2, 2, 2, 1));
  })();
  mat.colorNode = Fn(() => {
    const q = uv().sub(0.5).mul(2 * SPRITE_HALF_PX);
    const prof = exp(dot(q, q).mul(-1 / (2 * sig2)));
    return vec4(vLight.mul(prof), 1);
  })();
  return { material: mat, uniforms: u };
}

/** A unit quad (±0.5) as an instanced geometry with the carpet's three per-point attributes at `capacity`. */
export function createCityLightsGeometry(capacity: number): {
  geometry: THREE.InstancedBufferGeometry; pos: THREE.InstancedBufferAttribute; col: THREE.InstancedBufferAttribute; info: THREE.InstancedBufferAttribute;
} {
  const quad = new THREE.PlaneGeometry(1, 1);
  const geo = new THREE.InstancedBufferGeometry();
  geo.setIndex(quad.getIndex());
  geo.setAttribute('position', quad.getAttribute('position'));
  geo.setAttribute('uv', quad.getAttribute('uv'));
  // THE DYNAMIC-USAGE LAW: rewritten only when the carpet is recomposed — Static + needsUpdate.
  const pos = new THREE.InstancedBufferAttribute(new Float32Array(capacity * 3), 3);
  const col = new THREE.InstancedBufferAttribute(new Uint8Array(capacity * 4), 4, true);
  const info = new THREE.InstancedBufferAttribute(new Uint8Array(capacity * 4), 4, true);
  geo.setAttribute('aPos', pos);
  geo.setAttribute('aCol', col);
  geo.setAttribute('aInfo', info);
  geo.instanceCount = 0;
  quad.dispose();
  return { geometry: geo, pos, col, info };
}

// ── the haze ─────────────────────────────────────────────────────────────────────────────────────────────────────
export const HAZE_DOMES = 4;
export interface CityHazeUniforms {
  /** Per dome: vec4(dirX, dirZ, peak, halfWidth), vec4(height, distM, glowR, on), vec3 tint. */
  domeA: Array<{ value: THREE.Vector4 }>;
  domeB: Array<{ value: THREE.Vector4 }>;
  tint: Array<{ value: THREE.Vector3 }>;
  /** Scene units of one sky unit (sqmToNits(22) × emissiveNits(1)) × the night's on-factor. */
  unitScene: V;
  /** The camera's uniform glow at the horizon (glow.ratio × 2.2 — the sky's glowShape(0)) and the night-floor lift. */
  glowHorizon: V;
  lift: V;
  gain: V;
}
/** The haze pass: a full-screen quad (PlaneGeometry(1,1), positions ±0.5 → clip ±1), additive, no depth test. */
export function createCityHazeMaterial(): { material: THREE.MeshBasicNodeMaterial; uniforms: CityHazeUniforms } {
  const u: CityHazeUniforms = {
    domeA: Array.from({ length: HAZE_DOMES }, () => uniform(new THREE.Vector4(1, 0, 0, 0.5)) as unknown as { value: THREE.Vector4 }),
    domeB: Array.from({ length: HAZE_DOMES }, () => uniform(new THREE.Vector4(0.1, 1e5, 1000, 0)) as unknown as { value: THREE.Vector4 }),
    tint: Array.from({ length: HAZE_DOMES }, () => uniform(new THREE.Vector3(1.32, 0.94, 0.55)) as unknown as { value: THREE.Vector3 }),
    unitScene: uniform(0) as unknown as V,
    glowHorizon: uniform(0) as unknown as V,
    lift: uniform(1) as unknown as V,
    gain: uniform(1) as unknown as V,
  };
  const mat = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, depthTest: false, blending: THREE.AdditiveBlending });
  mat.name = 'city-night-haze';
  mat.fog = false;
  markMrtOverlay(mat);
  const vNdc = varyingProperty('vec2', 'vHazeNdc');
  mat.vertexNode = Fn(() => {
    const xy = positionGeometry.xy.mul(2);
    vNdc.assign(xy);
    return vec4(xy, 0.5, 1);
  })();
  mat.colorNode = Fn(() => {
    const pv = cameraProjectionMatrixInverse.mul(vec4(vNdc, 1, 1));
    const dirView = normalize(pv.xyz.div(pv.w));
    const dir = normalize(cameraWorldMatrix.mul(vec4(dirView, 0)).xyz).toVar('hzDir');
    const raw = viewportDepthTexture();
    const geo = geometryMask(raw as never) as N;
    // The hit distance reads THIS pass's own snapshot (`raw`), never three's shared `viewportLinearDepth`: the shared one is
    // copied at the first object that reads it, and the engine's OPAQUE water does — so with water in view it lacked every
    // solid drawn after the water, and this layer (depth test off) laid its haze over them (lib/vfx/scene_depth.ts, 2026-10-08).
    const viewZ = (linearDepth(raw as never) as N).mul(cameraFar.sub(cameraNear)).add(cameraNear);
    const hit = viewZ.div(max(dirView.z.negate(), float(1e-3))).toVar('hzHit');
    const h = length(dir.xz).max(1e-6);
    const elev = atan(dir.y, h);
    let acc = vec3(0, 0, 0) as unknown as ReturnType<typeof vec3>;
    let Hs = float(0) as unknown as N;
    for (let k = 0; k < HAZE_DOMES; k++) {
      const A = (u.domeA[k] as unknown as ReturnType<typeof vec4>), B = (u.domeB[k] as unknown as ReturnType<typeof vec4>);
      const c = dir.x.mul(A.x).add(dir.z.mul(A.y)).div(h);
      const dAz = acos(clamp(c, -1, 1));
      const azW = exp(dAz.div(A.w).mul(dAz.div(A.w)).mul(-0.5));
      const top = exp(float(-Math.PI / 2).div(B.x));
      const up = max(float(0), exp(elev.negate().div(B.x)).sub(top).div(float(1).sub(top)));
      const hDown = max(float(0.02), atan(float(HAZE_LAYER_M), B.y));
      const down = exp(elev.div(hDown));
      const prof = select(elev.greaterThanEqual(0), up, down);
      const front = smoothstep(B.y.sub(B.z), B.y.add(B.z), hit);
      const inside = clamp(B.y.sub(B.z).div(B.z), 0, 1);
      const r = A.z.mul(azW).mul(prof).mul(front).mul(inside).mul(B.w);
      acc = acc.add((u.tint[k] as unknown as ReturnType<typeof vec3>).mul(r)) as unknown as ReturnType<typeof vec3>;
      Hs = Hs.add(r) as unknown as N;
    }
    // THE HAZE'S DISPLAY (lib hazeDisplayUnits): the adapted excess the domes add over the uniform glow, × the lift
    const gs = u.glowHorizon as unknown as N;
    const disp = log(float(1).add(gs.add(Hs).div(HAZE_ART_K))).sub(log(float(1).add(gs.div(HAZE_ART_K)))).mul(HAZE_ART_K).mul(u.lift as unknown as N);
    const tint = acc.div(Hs.max(1e-6));
    return vec4(tint.mul(disp).mul(geo).mul((u.unitScene as unknown as N).mul(u.gain as unknown as N)), 1);
  })();
  return { material: mat, uniforms: u };
}
